import {
  Box,
  Button,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { find, isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useMutation } from 'urql';

import { useGetLoggedInUser, useUserTrialDetails } from '@revelio/auth';
import { useTourStore } from '@revelio/core';
import {
  type KeyValueResp,
  MetadataKey,
  type User,
} from '@revelio/data-access';

import { UpdateUserMutation } from '../adminRewrite/userOperations';
import tutorialThumbnail from './tutorial-thumbnail.jpg';
import styles from './welcome-modal.module.css';

const Welcome = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    loggedInUser,
    query: [{ fetching }],
    refetch: refetchUserData,
  } = useGetLoggedInUser();

  const [{ fetching: updatingUserMetaData }, updateUser] =
    useMutation(UpdateUserMutation);

  const { isTrialUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });

  const isTourComplete = useTourStore((s) => s.isTourComplete);
  const setIsVideoOpen = useTourStore((s) => s.setIsVideoOpen);
  const setIsTourComplete = useTourStore((s) => s.setIsTourComplete);
  const setIsTour = useTourStore((s) => s.setIsTour);

  useEffect(() => {
    if (isTourComplete) {
      onClose();
    }
  }, [isTourComplete, onClose]);

  const handleUpdateMetadata = (
    loggedInUser: User = {},
    newMeta: KeyValueResp[]
  ) => {
    const metaFieldsToExclude = [MetadataKey.ExpirationDate];

    return new Promise((resolve, reject) => {
      if (isEmpty(loggedInUser)) {
        reject('No logged in user provided');
      }

      const newMetaKeys = newMeta.map((item) => item.key);

      const currentMetaObjects = (loggedInUser.metadata || []).filter(
        (item: KeyValueResp | null) =>
          !newMetaKeys.includes(item?.key) &&
          !metaFieldsToExclude.includes(item?.key as MetadataKey)
      );

      const updatedMeta = [...currentMetaObjects, ...newMeta];

      updateUser({
        id: loggedInUser?.id as string,
        metadata: updatedMeta,
      })
        .then(() => {
          // update user object on update success
          refetchUserData();
          resolve('User updated');
        })
        .catch(() => {
          reject('Something went wrong');
        });
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    const isLocalTourComplete = useTourStore.getState().isTourComplete;

    const isEmptyUser = isEmpty(loggedInUser);

    const shouldNotStartTour =
      fetching || isEmptyUser || updatingUserMetaData || isLocalTourComplete;

    if (shouldNotStartTour) {
      return;
    }

    const metadata = loggedInUser.metadata || [];
    const tourCompleted = find(metadata, {
      key: 'tour_completed',
    }) as KeyValueResp;
    const isTourCompleted = tourCompleted?.value === 'true';

    if (!isTourCompleted) {
      onOpen();
      return;
    }
  }, [
    fetching,
    loggedInUser,
    updatingUserMetaData,
    isTourComplete,
    isTrialUser,
  ]);

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={() => null}
      isCentered
    >
      <ModalOverlay bgColor="rgba(0,0,0,0.7)" />
      <ModalContent className={styles.content}>
        <ModalBody pb={6}>
          <Box
            textAlign="center"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Text fontSize={26} fontWeight="semibold">
              Welcome to Revelio Labs
            </Text>
            <Text fontSize={18} opacity={0.7}>
              Let's get started on a tour of the dashboard
            </Text>
            <Button
              variant="link"
              _focus={{ boxShadow: 'none' }}
              cursor="pointer"
              _hover={{
                opacity: 0.8,
              }}
              onClick={() => {
                onClose();
                setIsVideoOpen(true);
                setIsTourComplete(true);
                handleUpdateMetadata(loggedInUser, [
                  {
                    key: MetadataKey.TourCompleted,
                    value: 'true',
                  },
                ]);
              }}
            >
              <Image
                src={tutorialThumbnail}
                alt="tutorial-thumbnail"
                mt={4}
                rounded="2xl"
              />
            </Button>
            <Button
              variant="link"
              size="sm"
              colorScheme="gray"
              className={styles.skipButton}
              mt={2}
              fontWeight="normal"
              onClick={() => {
                onClose();
                setIsTour(true);
              }}
              _focus={{ boxShadow: 'none' }}
            >
              No thanks, I'll read the quick tips
            </Button>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default Welcome;
